import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, imgUrl, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck, Check, Clock, CreditCard, MapPin, MessageCircle, Route } from "lucide-react";

function TourTrustIcon({ index }) {
  const Icons = [BadgeCheck, Route, MessageCircle, CreditCard];
  const Cmp = Icons[index] || BadgeCheck;
  return <Cmp size={16} />;
}

export default function TourDetail() {
  const { slug } = useParams();
  const { t, pick } = useLang();
  const [tour, setTour] = useState(null);
  const [dates, setDates] = useState([]); // array of "YYYY-MM-DD"
  const [selected, setSelected] = useState();
  const [form, setForm] = useState({ name: "", email: "", whatsapp: "", guests: 2, message: "", website: "" });
  const [formStartedAt, setFormStartedAt] = useState(() => Date.now() / 1000);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setTour(null);
    setSent(false);
    setSelected(undefined);
    setFormStartedAt(Date.now() / 1000);
    api.get(`/tours/${slug}`).then((r) => setTour(r.data)).catch(() => {});
    api.get(`/tours/${slug}/dates`).then((r) => setDates(r.data || [])).catch(() => {});
  }, [slug]);

  const availableSet = useMemo(() => new Set(dates), [dates]);

  const dateMatcher = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return availableSet.has(`${y}-${m}-${d}`);
  };

  const formattedSelected = useMemo(() => {
    if (!selected) return "";
    const y = selected.getFullYear();
    const m = String(selected.getMonth() + 1).padStart(2, "0");
    const d = String(selected.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [selected]);

  const submit = async (e) => {
    e.preventDefault();
    if (!tour) return;
    if (!formattedSelected) {
      toast.error("Please select an available date");
      return;
    }
    if (!form.name || !form.email || !form.whatsapp) {
      toast.error("Please fill in name, email and WhatsApp");
      return;
    }
    if (Date.now() / 1000 - formStartedAt < 3) {
      toast.error(t.form.wait);
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bookings", {
        tour_id: tour.id,
        date: formattedSelected,
        guests: Number(form.guests),
        name: form.name,
        email: form.email,
        whatsapp: form.whatsapp,
        message: form.message,
        website: form.website,
        form_started_at: formStartedAt,
      });
      setSent(true);
      toast.success(t.tour.booked_title);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (!tour) return <div className="pt-40 px-6 text-center text-charcoal-400">Loading…</div>;

  return (
    <div data-testid="tour-detail-page" className="pt-24">
      {/* Hero */}
      <div className="relative h-[48svh] md:h-[58svh] overflow-hidden">
        <img src={imgUrl(tour.hero_image)} alt={pick(tour.title_en, tour.title_fr)} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-8 md:pb-12">
          <div className="max-w-7xl mx-auto">
            <Link to="/tours" className="inline-flex items-center gap-2 text-sand-50/80 hover:text-sand-50 mb-4 text-sm" data-testid="back-to-tours">
              <ArrowLeft size={16} /> {t.tour.back}
            </Link>
            <div className="flex items-center gap-4 text-sand-50/80 text-xs uppercase tracking-widest mb-4">
              <span className="inline-flex items-center gap-1"><MapPin size={12} /> {tour.location}</span>
              <span className="inline-flex items-center gap-1"><Clock size={12} /> {tour.duration}</span>
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl tracking-tight font-light text-sand-50 leading-[1.05] max-w-4xl">
              {pick(tour.title_en, tour.title_fr)}
            </h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16 grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-14">
        <div className="lg:col-span-7 space-y-12">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.tour.overview}</p>
            <p className="font-serif-display text-2xl md:text-3xl text-charcoal leading-[1.4] font-light">
              {pick(tour.short_desc_en, tour.short_desc_fr)}
            </p>
            <div className="mt-6 text-charcoal-400 text-base md:text-lg leading-relaxed whitespace-pre-line">
              {pick(tour.long_desc_en, tour.long_desc_fr)}
            </div>
          </div>

          {(tour.inclusions_en?.length || tour.inclusions_fr?.length) > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-4">{t.tour.includes}</p>
              <ul className="space-y-3">
                {(pick(tour.inclusions_en, tour.inclusions_fr) || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-charcoal-400">
                    <Check size={18} className="text-terracotta mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Booking Sidebar */}
        <aside className="lg:col-span-5">
          <div className="lg:sticky lg:top-28 bg-sand-100 p-7 md:p-10">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs uppercase tracking-widest text-charcoal-400">{t.home.from}</span>
            </div>
            <div className="flex items-baseline gap-2 mb-8">
              <span className="font-serif-display text-5xl text-charcoal">€{tour.price}</span>
              <span className="text-charcoal-400 text-sm">/ {t.tour.per_group}</span>
            </div>

            <div className="mb-7 rounded-md border border-charcoal/10 bg-white p-4">
              <h2 className="text-sm font-semibold text-charcoal">{t.tour.trust_title}</h2>
              <div className="mt-3 grid grid-cols-1 gap-2">
                {t.tour.trust_items.map((item, i) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-charcoal-400">
                    <span className="text-terracotta"><TourTrustIcon index={i} /></span>
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {sent ? (
              <div data-testid="booking-success" className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mb-4">
                  <Check size={28} />
                </div>
                <h3 className="font-serif-display text-2xl text-charcoal mb-2">{t.tour.booked_title}</h3>
                <p className="text-charcoal-400 text-sm">{t.tour.booked_sub}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="booking-form" className="space-y-5">
                <div className="absolute -left-[10000px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                  <Label htmlFor="booking-website">Website</Label>
                  <Input
                    id="booking-website"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
                <p className="rounded-md border border-terracotta/20 bg-terracotta/10 px-4 py-3 text-xs leading-relaxed text-charcoal">
                  {t.form.reassurance}
                </p>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-charcoal-400">{t.tour.select_date}</Label>
                  <div className="mt-3 rounded-md bg-white border border-charcoal/10 p-2">
                    {dates.length === 0 ? (
                      <p className="text-xs text-charcoal-400 p-4">{t.tour.no_dates}</p>
                    ) : (
                      <Calendar
                        mode="single"
                        selected={selected}
                        onSelect={setSelected}
                        disabled={(d) => !dateMatcher(d)}
                        fromDate={new Date()}
                        modifiersClassNames={{ selected: "bg-terracotta text-white" }}
                        data-testid="booking-calendar"
                      />
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="guests" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.guests}</Label>
                    <Input id="guests" data-testid="booking-guests" type="number" min={1} max={20} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} className="mt-2 bg-white" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.date}</Label>
                    <Input value={formattedSelected} readOnly className="mt-2 bg-white" data-testid="booking-date-display" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.name}</Label>
                  <Input id="name" data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-white" required />
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.email}</Label>
                  <Input id="email" data-testid="booking-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-white" required />
                </div>

                <div>
                  <Label htmlFor="whatsapp" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.whatsapp}</Label>
                  <Input id="whatsapp" data-testid="booking-whatsapp" value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} className="mt-2 bg-white" required />
                </div>

                <div>
                  <Label htmlFor="msg" className="text-xs uppercase tracking-widest text-charcoal-400">{t.form.message}</Label>
                  <Textarea id="msg" data-testid="booking-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 bg-white" />
                </div>

                <button
                  type="submit"
                  data-testid="booking-submit"
                  disabled={submitting}
                  className="w-full bg-terracotta hover:bg-terracotta-600 disabled:opacity-60 text-sand-50 rounded-full py-3.5 transition mt-2"
                >
                  {submitting ? "…" : t.tour.book_btn}
                </button>
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
